import React, { useState, useMemo } from 'react';
import { Patient } from '../types/patient';
import { FlaskConical, Calendar, TrendingUp, TrendingDown, ChevronDown, ChevronRight, BarChart3, List } from 'lucide-react';
import { format } from 'date-fns';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, ReferenceLine } from 'recharts';
import { getBadgeClasses, getStatusIcon } from '../utils/styleHelpers';

interface LabResultsProps {
  patient: Patient;
}

type ViewMode = 'byDate' | 'byTest';

export default function LabResults({ patient }: LabResultsProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('byDate');
  const [filterStatus, setFilterStatus] = useState<'all' | 'normal' | 'abnormal' | 'critical'>('all');
  const [selectedTest, setSelectedTest] = useState<string>('');
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [selectedDate, setSelectedDate] = useState<string>('all');

  // Get all unique test names
  const testNames = useMemo(() => {
    const names = new Set<string>();
    patient.labResults.forEach(result => names.add(result.name));
    return Array.from(names).sort();
  }, [patient.labResults]);

  // Get all unique dates
  const uniqueDates = useMemo(() => {
    const dates = new Set<string>();
    patient.labResults.forEach(result => dates.add(result.date));
    return Array.from(dates).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
  }, [patient.labResults]);

  // Group lab results by date
  const labsByDate = useMemo(() => {
    const groups = new Map<string, typeof patient.labResults>();

    patient.labResults.forEach(result => {
      if (!groups.has(result.date)) {
        groups.set(result.date, []);
      }
      groups.get(result.date)!.push(result);
    });

    // Sort dates descending (most recent first)
    const sortedEntries = Array.from(groups.entries())
      .sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime());

    return new Map(sortedEntries);
  }, [patient.labResults]);

  // Group lab results by test name for trends
  const labsByTest = useMemo(() => {
    const groups = new Map<string, typeof patient.labResults>();

    patient.labResults.forEach(result => {
      if (!groups.has(result.name)) {
        groups.set(result.name, []);
      }
      groups.get(result.name)!.push(result);
    });

    // Sort each group by date (oldest first for charts)
    groups.forEach((results, name) => {
      results.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    });

    return groups;
  }, [patient.labResults]);

  // Filter results based on status and date
  const getFilteredResults = (results: typeof patient.labResults) => {
    return results.filter(result => {
      if (filterStatus !== 'all' && result.status !== filterStatus) {
        return false;
      }
      if (selectedDate !== 'all' && result.date !== selectedDate) {
        return false;
      }
      return true;
    });
  };

  // Toggle date expansion
  const toggleDate = (date: string) => {
    const newExpanded = new Set(expandedDates);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedDates(newExpanded);
  };

  // Expand all dates
  const expandAllDates = () => {
    setExpandedDates(new Set(uniqueDates));
  };

  // Collapse all dates
  const collapseAllDates = () => {
    setExpandedDates(new Set());
  };

  const MiniTrendChart = ({ data, status }: { data: any[], status: string }) => {
    const color = status === 'critical' ? '#ef4444' : status === 'abnormal' ? '#f59e0b' : '#22c55e';
    return (
      <div className="h-12 w-24">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <Line
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const FullTrendChart = ({ testName }: { testName: string }) => {
    const results = labsByTest.get(testName) || [];
    if (results.length === 0) return null;

    const chartData = results.map(r => ({
      date: format(new Date(r.date), 'MMM dd'),
      value: r.value,
      status: r.status,
      fullDate: r.date
    }));

    // Get reference range for the test
    const refRange = results[0]?.referenceRange || '';
    const [low, high] = refRange.split('-').map(s => parseFloat(s.trim()));

    const color = results[results.length - 1]?.status === 'critical'
      ? '#ef4444'
      : results[results.length - 1]?.status === 'abnormal'
        ? '#f59e0b'
        : '#22c55e';

    return (
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              stroke="#6b7280"
            />
            <YAxis
              tick={{ fontSize: 12 }}
              stroke="#6b7280"
              domain={['auto', 'auto']}
            />
            <Tooltip
              formatter={(value: number) => [value, 'Value']}
              labelFormatter={(label) => `Date: ${label}`}
            />
            {!isNaN(low) && (
              <ReferenceLine
                y={low}
                stroke="#22c55e"
                strokeDasharray="3 3"
                label={{ value: 'Low', position: 'left', fontSize: 10 }}
              />
            )}
            {!isNaN(high) && (
              <ReferenceLine
                y={high}
                stroke="#22c55e"
                strokeDasharray="3 3"
                label={{ value: 'High', position: 'left', fontSize: 10 }}
              />
            )}
            <Line
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              dot={{ fill: color, r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header and Controls */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center space-x-2">
          <FlaskConical className="w-5 h-5 text-brand-dark-500" />
          <h2 className="text-xl font-bold text-brand-dark-600">Lab Results</h2>
        </div>

        <div className="flex items-center space-x-4">
          {/* View Mode Selector */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-brand-taupe-600">View:</span>
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value as ViewMode)}
              className="px-3 py-1 border border-brand-taupe-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-brand-dark-400 focus:border-brand-dark-500 outline-none"
            >
              <option value="byDate">By Date</option>
              <option value="byTest">By Test (Trends)</option>
            </select>
          </div>

          {/* Status Filter */}
          <div className="flex space-x-1">
            {(['all', 'critical', 'abnormal', 'normal'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === status
                    ? 'bg-brand-dark-500 text-white'
                    : 'bg-brand-beige-200 text-brand-dark-600 hover:bg-brand-beige-300'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* View Mode Content */}
      {viewMode === 'byDate' ? (
        /* BY DATE VIEW */
        <div className="space-y-4">
          {/* Date Filter and Expand/Collapse Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <label className="text-sm text-brand-taupe-600">Filter by date:</label>
              <select
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-3 py-1 border border-brand-taupe-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-brand-dark-400 focus:border-brand-dark-500 outline-none"
              >
                <option value="all">All Dates</option>
                {uniqueDates.map(date => (
                  <option key={date} value={date}>
                    {format(new Date(date), 'MMM dd, yyyy')}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={expandAllDates}
                className="text-sm text-brand-dark-500 hover:text-brand-dark-600"
              >
                Expand All
              </button>
              <span className="text-brand-taupe-400">|</span>
              <button
                onClick={collapseAllDates}
                className="text-sm text-brand-dark-500 hover:text-brand-dark-600"
              >
                Collapse All
              </button>
            </div>
          </div>

          {/* Date Groups */}
          {Array.from(labsByDate.entries())
            .filter(([date]) => selectedDate === 'all' || date === selectedDate)
            .map(([date, results]) => {
              const filteredResults = getFilteredResults(results);
              if (filteredResults.length === 0 && filterStatus !== 'all') return null;

              const isExpanded = expandedDates.has(date);
              const criticalCount = results.filter(r => r.status === 'critical').length;
              const abnormalCount = results.filter(r => r.status === 'abnormal').length;

              return (
                <div key={date} className="card">
                  <button
                    onClick={() => toggleDate(date)}
                    className="w-full flex items-center justify-between p-2 -m-2 hover:bg-brand-beige-50 rounded-lg transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-brand-taupe-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-brand-taupe-500" />
                      )}
                      <Calendar className="w-5 h-5 text-brand-dark-500" />
                      <span className="font-semibold text-brand-dark-600">
                        {format(new Date(date), 'MMMM dd, yyyy')}
                      </span>
                      <span className="text-sm text-brand-taupe-600">
                        ({results.length} {results.length === 1 ? 'test' : 'tests'})
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {criticalCount > 0 && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-danger-100 text-danger-700 rounded-full">
                          {criticalCount} Critical
                        </span>
                      )}
                      {abnormalCount > 0 && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-warning-100 text-warning-700 rounded-full">
                          {abnormalCount} Abnormal
                        </span>
                      )}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-brand-taupe-200">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-brand-beige-100">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-brand-dark-600 uppercase">Test</th>
                              <th className="px-4 py-2 text-center text-xs font-semibold text-brand-dark-600 uppercase">Value</th>
                              <th className="px-4 py-2 text-center text-xs font-semibold text-brand-dark-600 uppercase">Reference</th>
                              <th className="px-4 py-2 text-center text-xs font-semibold text-brand-dark-600 uppercase">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-brand-taupe-200">
                            {(filterStatus === 'all' ? results : filteredResults).map((result) => {
                              const StatusIcon = getStatusIcon(result.status as any);
                              return (
                                <tr key={result.id} className="hover:bg-brand-ivory-100">
                                  <td className="px-4 py-2 text-sm font-medium text-brand-dark-600">{result.name}</td>
                                  <td className="px-4 py-2 text-center">
                                    <span className="font-bold text-brand-dark-600">{result.value} {result.unit}</span>
                                  </td>
                                  <td className="px-4 py-2 text-center text-sm text-brand-taupe-600">{result.referenceRange}</td>
                                  <td className="px-4 py-2 text-center">
                                    <div className="flex justify-center">
                                      <div className={getBadgeClasses(result.status as any, 'sm')}>
                                        <StatusIcon className="w-3 h-3 mr-1" aria-hidden="true" />
                                        <span className="capitalize">{result.status}</span>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

          {/* Empty State */}
          {labsByDate.size === 0 && (
            <div className="card text-center py-12">
              <FlaskConical className="w-12 h-12 text-brand-taupe-400 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-brand-dark-600 mb-2">No lab results found</h4>
              <p className="text-brand-taupe-600">No lab results are available for this patient.</p>
            </div>
          )}
        </div>
      ) : (
        /* BY TEST (TRENDS) VIEW */
        <div className="space-y-4">
          {/* Test Selector */}
          <div className="card">
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-brand-dark-600">Select Test:</label>
              <select
                value={selectedTest}
                onChange={(e) => setSelectedTest(e.target.value)}
                className="flex-1 max-w-md px-3 py-2 border border-brand-taupe-300 rounded-lg bg-white focus:ring-2 focus:ring-brand-dark-400 focus:border-brand-dark-500 outline-none"
              >
                <option value="">Choose a test to view trends...</option>
                {testNames.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Trend Chart and History */}
          {selectedTest ? (
            <div className="space-y-4">
              {/* Trend Chart */}
              <div className="card">
                <h3 className="text-lg font-semibold text-brand-dark-600 mb-4">
                  {selectedTest} - Historical Trend
                </h3>
                <FullTrendChart testName={selectedTest} />
              </div>

              {/* Historical Values Table */}
              <div className="card">
                <h3 className="text-lg font-semibold text-brand-dark-600 mb-4">
                  Historical Values
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-brand-beige-100 border-b-2 border-brand-taupe-300">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-brand-dark-600 uppercase">Date</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-brand-dark-600 uppercase">Value</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-brand-dark-600 uppercase">Reference Range</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-brand-dark-600 uppercase">Status</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-brand-dark-600 uppercase">Change</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-brand-taupe-200">
                      {(labsByTest.get(selectedTest) || [])
                        .slice()
                        .reverse()
                        .map((result, index, arr) => {
                          const StatusIcon = getStatusIcon(result.status as any);
                          const prevResult = arr[index + 1];
                          const change = prevResult
                            ? ((result.value - prevResult.value) / prevResult.value * 100).toFixed(1)
                            : null;
                          const changeIsPositive = change && parseFloat(change) > 0;

                          return (
                            <tr key={result.id} className="hover:bg-brand-ivory-100 transition-colors">
                              <td className="px-4 py-3 text-sm font-medium text-brand-dark-600">
                                {format(new Date(result.date), 'MMM dd, yyyy')}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="font-bold text-brand-dark-600">{result.value} {result.unit}</span>
                              </td>
                              <td className="px-4 py-3 text-center text-sm text-brand-taupe-600">
                                {result.referenceRange}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <div className="flex justify-center">
                                  <div className={getBadgeClasses(result.status as any, 'sm')}>
                                    <StatusIcon className="w-3 h-3 mr-1" aria-hidden="true" />
                                    <span className="capitalize">{result.status}</span>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-center">
                                {change ? (
                                  <span className={`flex items-center justify-center text-sm font-medium ${
                                    changeIsPositive ? 'text-danger-600' : 'text-success-600'
                                  }`}>
                                    {changeIsPositive ? (
                                      <TrendingUp className="w-4 h-4 mr-1" />
                                    ) : (
                                      <TrendingDown className="w-4 h-4 mr-1" />
                                    )}
                                    {changeIsPositive ? '+' : ''}{change}%
                                  </span>
                                ) : (
                                  <span className="text-sm text-brand-taupe-400">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="card text-center py-12">
              <BarChart3 className="w-12 h-12 text-brand-taupe-400 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-brand-dark-600 mb-2">Select a Test</h4>
              <p className="text-brand-taupe-600">
                Choose a lab test from the dropdown above to view its historical trend.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card text-center bg-gradient-to-br from-success-50 to-success-100">
          <div className="text-3xl font-bold text-success-700">
            {patient.labResults.filter(r => r.status === 'normal').length}
          </div>
          <div className="text-sm text-success-900 font-medium mt-1">Normal Results</div>
        </div>

        <div className="card text-center bg-gradient-to-br from-warning-50 to-warning-100">
          <div className="text-3xl font-bold text-warning-700">
            {patient.labResults.filter(r => r.status === 'abnormal').length}
          </div>
          <div className="text-sm text-warning-900 font-medium mt-1">Abnormal Results</div>
        </div>

        <div className="card text-center bg-gradient-to-br from-danger-50 to-danger-100">
          <div className="text-3xl font-bold text-danger-700">
            {patient.labResults.filter(r => r.status === 'critical').length}
          </div>
          <div className="text-sm text-danger-900 font-medium mt-1">Critical Results</div>
        </div>
      </div>
    </div>
  );
}
